<template>
  <nav class="sh-messaging-conv-list">
    <div
      v-for="thread in displayThreads"
      :class="['sh-conv-row', { 'is-active': active == thread.id, 'is-blocked': isBlockedThread(thread) }]"
      :key="thread.id"
      @click="selectThread(thread)"
    >
      <i v-if="isBlockedThread(thread)" class="fal fa-ban sh-conv-row-blocked-icon" aria-label="Blocked user"></i>
      <div :class="['sh-conv-row-avatar', { 'sh-avatar-stack': thread.isGroup }]">
        <UserAvatar
          v-for="uid in avatarUserIds(thread)"
          :key="uid"
          :pUserId="uid"
          :pSize="thread.isGroup ? '20px' : '32px'"
          :p-line-height="thread.isGroup ? '20px' : '2rem'"
          :p-font-size="thread.isGroup ? '0.6rem' : '1rem'"
          type="author-avatar"
        />
        <span v-if="overflowCount(thread) > 0" class="sh-avatar-overflow">
          +{{ overflowCount(thread) }}
        </span>
      </div>

      <div class="sh-conv-row-body">
        <template v-if="isBlockedThread(thread)">
          <span class="sh-conv-row-names">Blocked User</span>
          <span class="sh-conv-row-rally">{{ threadDisplayName(thread) }}</span>
        </template>
        <template v-else>
          <span class="sh-conv-row-names">
            {{ threadDisplayName(thread) }}
          </span>
          <span v-if="rallyName(thread)" class="sh-conv-row-rally">
            {{ rallyName(thread) }}
          </span>
        </template>
      </div>

      <span
        v-if="isUnread(thread)"
        class="sh-conv-row-dot"
        aria-label="Unread messages"
      />

      <i class="fal fa-chevron-right sh-conv-row-chevron" aria-hidden="true"></i>
    </div>
  </nav>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import UserAvatar from "./UserAvatar.vue";

  export default {
    components: { UserAvatar },
    props: {
      active: [Number, String],
      threads: Array,
    },
    computed: {
      ...mapState("user", ["user", "status", "usersById"]),
      ...mapState("channels", ["newPrivateMessageCountBySenderName", "unreadCountByChannelId"]),
      ...mapState("rallies", ["allRalliesByRallyId"]),
      ...mapState("contents", ["allContentByContextTypeAndId"]),
      ...mapGetters("blockedUsers", ["isUserBlocked"]),
      displayThreads(){
        // Hide 1:1 rows whose recipient hasn't hydrated yet (legacy
        // behavior); group rows render as soon as any participant resolves.
        const visible = (this.threads || []).filter((t) => {
          if (t.isGroup) { return t.participantUserIds.length > 0 }
          const u = this.participantUsers(t)[0]
          return u && u.attributes && u.attributes['last-name'] && (u.attributes['last-name'] + "") !== "undefined"
        });
        // Most recent activity first. Reactive on the contents store, so a
        // newly sent/received message bumps its thread to the top live.
        return visible.sort((a, b) => this.lastActivityAt(b) - this.lastActivityAt(a));
      }
    },
    methods: {
      ...mapActions("channels", ["clearPrivateMessageCountByUserName"]),
      // Epoch millis of the thread's latest message; falls back to the
      // channel's own timestamps when no messages are loaded yet (fresh
      // thread, or contents still fetching).
      lastActivityAt(thread){
        const cs = this.allContentByContextTypeAndId[thread.id] || [];
        let latest = 0;
        for (const c of cs) {
          const t = Date.parse(c?.attributes?.['created-at'] || '');
          if (t && t > latest) { latest = t; }
        }
        if (!latest) {
          const ch = thread.channel && thread.channel.attributes;
          latest = Date.parse((ch && (ch['updated-at'] || ch['created-at'])) || '') || 0;
        }
        return latest;
      },
      participantUsers(thread){
        const me = this.user && (this.user.user_id + "")
        return thread.participantUserIds
          .filter(uid => (uid + "") !== me) // defensive: self never renders as a participant
          .map(uid => this.usersById[uid])
          .filter(u => u && u.attributes)
      },
      // A 1:1 thread whose other party the current user has blocked. The row
      // shows "Blocked User" with the real name demoted to the subtitle.
      isBlockedThread(thread){
        if (!thread || thread.isGroup) { return false }
        const ids = thread.participantUserIds || []
        return ids.length === 1 && this.isUserBlocked(ids[0])
      },
      threadDisplayName(thread){
        // A renamed group shows its custom name; otherwise (and for 1:1s,
        // whose names are always the service's auto shape) the participant
        // names.
        const n = thread?.channel?.attributes?.name
        if (n && !n.startsWith('GroupMessage:') && !n.startsWith('PrivateMessage:')) { return n }
        const users = this.participantUsers(thread)
        if (users.length === 0) { return "…" }
        return users
          .map(u => `${u.attributes['first-name']} ${u.attributes['last-name']}`)
          .join(', ')
      },
      // Cluster layout: exactly 3 members -> three small circles; more than
      // 3 -> two circles plus a "+N" badge in the third slot.
      avatarUserIds(thread){
        const ids = thread.participantUserIds
        return ids.length === 3 ? ids.slice(0, 3) : ids.slice(0, 2)
      },
      overflowCount(thread){
        const n = thread.participantUserIds.length
        return n > 3 ? n - 2 : 0
      },
      rallyName(thread){
        const r = this.allRalliesByRallyId && this.allRalliesByRallyId[thread.rallyId]
        return r?.rally?.attributes?.title || r?.attributes?.title || null
      },
      selectThread(thread){
        // Legacy unread counts are keyed by sender name and only exist for
        // 1:1 threads.
        if (!thread.isGroup) {
          const u = this.participantUsers(thread)[0]
          if (u) {
            this.clearPrivateMessageCountByUserName({name: u.attributes['first-name'] + " " + u.attributes['last-name']})
          }
        }
        this.$emit('perform', 'selectThread', thread.id)
      },
      // Green unread dot. Primary source is the server-derived per-channel
      // count from rns (accurate, covers group threads, survives reload);
      // the legacy push-scraped counter is kept only as an instant signal on
      // native between unread-count polls.
      isUnread(thread){
        if ((this.unreadCountByChannelId[thread.id + ""] || 0) > 0) { return true }
        return this.newCount(thread) > 0
      },
      newCount(thread){
        if (thread.isGroup) { return 0 }
        const u = this.participantUsers(thread)[0]
        if (!u) { return 0 }
        return this.newPrivateMessageCountBySenderName[u.attributes['first-name'] + " " + u.attributes['last-name']]
      }
    }
  };
</script>

<style scoped>
/* Cluster layout and the "+N" overflow badge live in sh-messaging.css
   (.sh-avatar-stack rules) — they're shared with the conversation header,
   and scoped CSS can't reach inside UserAvatar's markup anyway. */
</style>
